/*!
 * Copyright 2017 - 2020 by ChartIQ, Inc.
 * All rights reserved.
 */

// ///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// Overview in Real Time Board
// https://realtimeboard.com/welcomeonboard/dqbvK59KjETgD8Yc2VmW4BSpCieLXc4jfwmFMkgFTVRqLba0sCDFO2H3PHlGCpqi
// ///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

import { Logger } from "../../clients/logger";
import { RouterClient } from "../../clients/routerClient";
import { ConfigClient } from "../../clients/configClient";
import { LauncherClient } from "../../clients/launcherClient";
import { ConfigUtilInstance as ConfigUtil } from "../../common/configUtil";
import { BootStage, BootDependencyType, BootConfigElement, BootReadyItem, BootDependencyState } from "./types";
import { BootCheckpointEngine, BootCheckpointResults } from "./bootCheckpointEngine";
import { BootDependencyGraph, DGGraphStatus, DGDiagnostics } from "./bootDependencyGraph";
import { ServiceLauncher } from "./serviceLauncher";
import { ShutdownManager } from "./shutDownManager";
import SystemLog from "./systemLog";
import * as Util from "../../common/util";
import { ALL_BOOT_STAGES, CRITICAL_BOOT_STAGES, STAGE_CHANNEL } from "./_constants";
import { statusChannel } from "./common";
import SystemManagerClient from "../../common/systemManagerClient";
import { FSBLService } from "../../clients/Startup/FSBLService";
import { StandardPromise } from "../../types";

const ALL_BOOT_DEPENDENCY_TYPES: BootDependencyType[] = ["bootTasks", "services", "components"];
const PROCESS_STATUS_COUNTER_MAX = 500;
const DEFAULT_STARTUP_TIMEOUT = 15000;
const UNITIALIZED_BOOT_STATE = { name: "", state: "uninitialized" };
const WAIT_FOR_LAST_CHECKPOINT_DELAY = 50;

type Manifest = Record<string, any>;
type FinsembleConfig = Record<string, any>;

/**
 * The boot engine brings up Finsemble in an orderly way considering dependencies, all based on the "bootConfig" defined in tasks, services, and components.
 *
 * Side Note: For explanation of TypeScript's mapped types in index signatures, seach for "mapped types" in https://basarat.gitbooks.io/typescript/docs/types/index-signatures.html.
 */
export class BootEngine {
	startServiceTimeout: number;

	// millisecond timeout value for starting a service
	startComponentTimeout: number;

	// millisecond timeout value for starting a comp0nent
	startTaskTimeout: number;

	// millisecond timeout value for starting a task
	currentStage: BootStage = "undefined";

	// current boot stage -- the one being activitely "processed"
	dependencyGraphs: { [property in BootStage]?: BootDependencyGraph } = {};

	// each stage's dependency graph saved here by stage -- see Note above
	bootConfigs: { [property in BootStage]?: BootConfigElement[] } = {};

	// contains each stages complete boot config
	cumulativePreviousBootConfig: BootConfigElement[] = [];

	// boot config for all previous stages
	stageResolver: Function | undefined;

	// for saving Promise.resolve
	stageRejecter: Function | undefined;

	// for saving Promise.reject
	startTimers: { [property in string]?: any } = {};

	// each time a dependency is started, it's "timeout" timer is saved here by dependency name
	registeredTasks: { [property in string]?: Function } = {};

	// contains all known registered tasks
	manifest: Manifest;

	// contains orignal system manifest
	finsembleConfig: FinsembleConfig;

	// contains finsemble config (will be updated with latest as needed)
	serviceLauncher: ServiceLauncher;

	// instance of service launcher
	processStatusCounter = 0;

	shutdownManager: ShutdownManager;

	// holds shutdown manager passed in on constructor
	processingTerminated = false;

	// indicates bootEngine has terminated -- used to stop system log output for late responses after terminating on error...to avoid clutter
	activeServices: string[] = [];

	// list of all active/started services
	checkpointEngines: { [property in string]: BootCheckpointEngine } = {}; // each dependency with checkpoints will have its own checkpoint engine

	/**
	 * Creates an instance of boot engine.
	 * @param finsembleConfig
	 */
	constructor(manifest: Manifest, serviceLauncher: ServiceLauncher, shutdownManager: ShutdownManager) {
		this.manifest = manifest;
		this.finsembleConfig = manifest.finsemble;
		this.serviceLauncher = serviceLauncher;
		this.shutdownManager = shutdownManager;
		this.genericHandlerForStateChange = this.genericHandlerForStateChange.bind(this);
		this.startServiceTimeout = ConfigUtil.getDefault(
			this.finsembleConfig,
			"finsembleConfig.bootConfig.defaults.startServiceTimeout",
			DEFAULT_STARTUP_TIMEOUT
		);
		this.startComponentTimeout = ConfigUtil.getDefault(
			this.finsembleConfig,
			"finsembleConfig.bootConfig.defaults.startComponentTimeout",
			DEFAULT_STARTUP_TIMEOUT
		);
		this.startTaskTimeout = ConfigUtil.getDefault(
			this.finsembleConfig,
			"finsembleConfig.bootConfig.defaults.startTaskTimeout",
			DEFAULT_STARTUP_TIMEOUT
		);
		this.setupDependencyBootStatusPubSubs();
	}

	/**
	 * Displays bootEngine's diagnostic data
	 */
	public helpData() {
		const stage = this.dependencyGraphs[this.currentStage];
		console.log("2) finsemble.bootConfig timeout values");
		console.log("\tstartServiceTimeout value", this.startServiceTimeout);
		console.log("\tstartComponentTimeout value", this.startComponentTimeout);
		console.log("\tstartTaskTimeout value", this.startTaskTimeout);
		console.log("");
		console.log("3) Current boot stage:", this.currentStage);
		console.log("");
		console.log(
			`4) Lastest/current status of dependencyGraph for ${this.currentStage} stage`,
			stage && stage.getCurrentStatus()
		);
		console.log("");
		console.log("5) Dependency graphs by stage", this.dependencyGraphs);
		console.log("");
		console.log("6) Boot config data by stage", this.bootConfigs);
		console.log("");
		console.log("7) Active Checkpoint Engines", this.checkpointEngines);
		console.log("");
		console.log("8) Registered tasks", this.registeredTasks);
		console.log("");
		console.log("9) List of outstanding start timers", this.startTimers);
		console.log("");
		console.log("10) Dependency graphs display by stage");
		this.outputDependencyGraphs(this.dependencyGraphs);
		console.log("");
	}

	/**
	 * Registers one boot task with the bootEngine. All tasks dependencies must have corresponding task registrations. In other words, the only way
	 * the bootEngine knows about a startup task is if it's been registered.
	 * @param taskName
	 * @param taskFunction
	 */
	public registerBootTask(taskName: string, taskFunction: Function) {
		if (typeof taskName === "string" && typeof taskFunction === "function") {
			this.registeredTasks[taskName] = taskFunction; // save the task function to be invoked when its becomes "readyToStart"
		} else {
			SystemLog.error(
				{},
				`BootEngine: registerBootTask failed because of illegal input.  Task Name = ${taskName}. Task Function Type = ${typeof taskFunction}`
			);
		}
	}

	/**
	 * Runs boot engine serially though each of the boot stages.  Status and diagnostics will go to the System Log.
	 */
	public async run() {
		try {
			// Sequentially walk through each boot stage
			for (let i = 0; i < ALL_BOOT_STAGES.length; i++) {
				const runBootStageCatch = (err: DGDiagnostics) => {
					this.logDiagnostics(err);

					// terminate going though boot stages if error in critical stages; otherwise will clutter syslog with errors
					if (CRITICAL_BOOT_STAGES.includes(this.currentStage)) {
						throw "*** Terminating Boot ***";
					}
				};
				this.currentStage = ALL_BOOT_STAGES[i];
				Logger.system.log("BootEngine.run stage entered", this.currentStage);

				// runs one boot stage
				await this.runBootStage(this.currentStage).catch(runBootStageCatch);

				// after each stage is ran, update config since might have underlying changes (e.g. once config service is available then full config can be queried; also config can dynamically
				// change after authentication); also, since the config service is started in the first microkernal stage, it will be available after the first stage is complete.
				const { data: finsembleConfig } = await ConfigClient.getValue({
					field: "finsemble",
				});
				this.finsembleConfig = finsembleConfig;

				// Add active services to the dependency manager so that they shut down cleanly
				this.shutdownManager.addActiveServicesToDependencyManager(this.activeServices);

				// publish the completion of each stage
				Logger.system.log("BootEngine.run stage complete", this.currentStage);
				RouterClient.publish(STAGE_CHANNEL, { stage: this.currentStage });
			}
		} catch (error) {
			// Even if some stages errored out, add properly loaded services to the dependency manager
			this.shutdownManager.addActiveServicesToDependencyManager(this.activeServices);

			// even though terminating, remove outstanding timers so they won't generate errors and clutter system log
			this.cleanupStagesOutStandingTimers();

			// processingTerminated will be used to stop processing of any incoming notifications
			this.processingTerminated = true;

			SystemLog.error({}, `"BootEngine: ${error}`);
		}
	}

	/**
	 * Programmatically start a service
	 * @param serviceName name of service to start (must have corresponding config in finsemble.services)
	 * @deprecated
	 */
	public async startServiceDeprecated(serviceName: string): StandardPromise<void> {
		const serviceConfigElement = this.manifest.finsembleConfig.services[serviceName];
		if (serviceConfigElement) {
			SystemLog.log({ indent: true }, `Starting service ${serviceName}`);
			Logger.system.debug(`bootEngine.startServiceDeprecated starting service ${serviceName} in ${this.currentStage}`);
			return await this.serviceLauncher.createService(serviceConfigElement);
		} else {
			const err = `"BootEngine.startServiceDeprecated failed for ${serviceName} because config not found in "finsemble.services"`;
			SystemLog.error({}, err);
			return { err };
		}
	}

	// /////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
	// The remaining methods are private
	// /////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

	/**
	 * Outputs diagnostics to SystemLog. Contains very specific formatting that will change when real SystemLog is available.
	 * @param err
	 */
	private logDiagnostics(err: DGDiagnostics) {
		SystemLog.error(
			{ leadingBlankLine: true, indent: true },
			`Error in stage "${this.currentStage}": ${err.description}`
		);

		for (let i = 0; i < err.errorData.length; i++) {
			SystemLog.error({ doubleIndent: true }, `"${i}: ${err.errorData[i]}`);
		}

		SystemLog.error(
			{ leadingBlankLine: true, indent: true },
			`"${this.currentStage}" stage failed. See SystemManager console for additional information.`
		);
	}

	/**
	 * Outputs checkpoint diagnostics to SystemLog.  Contains very specific formatting that will change when real SystemLog is available.
	 * @param err
	 */
	private checkpointLogDiagnostics(err: DGDiagnostics) {
		if (err.description) {
			SystemLog.warn({ indent: true }, `checkpoint issue in stage "${this.currentStage}": ${err.description}`);
			for (let i = 0; i < err.errorData.length; i++) {
				SystemLog.warn({ doubleIndent: true }, `${i}: ${err.errorData[i]}`);
			}
		} else {
			for (let i = 0; i < err.errorData.length; i++) {
				SystemLog.warn({ indent: true }, `${err.errorData[i]}`);
			}
		}
	}

	/**
	 * Finds config element and returns its value (or null if not found)
	 * @param name
	 * @returns
	 */
	private findConfigElement(name: string): BootConfigElement | null {
		const stage = this.bootConfigs[this.currentStage];
		if (stage) {
			const index = stage.findIndex((element) => element.name === name);
			const configElement = stage[index];

			// the assumption is a config element will always be available for the give name
			if (!configElement) {
				Logger.system.error("bootEngine.findConfigElement could not find expected config element", name, stage);
			}
			return configElement;
		} else {
			return null;
		}
	}

	/**
	 * Outputs all dependency graphs to console -- this is purely for diagnostics
	 * @param dependencyGraphs map of graphs by stage
	 */
	private outputDependencyGraphs(dependencyGraphs: { [property in BootStage]?: BootDependencyGraph }) {
		for (let i = 0; i < ALL_BOOT_STAGES.length; i++) {
			const stage = ALL_BOOT_STAGES[i];
			const oneGraph = dependencyGraphs[stage];
			if (oneGraph) {
				console.log("\tStage:", stage);
				for (const nodeName in oneGraph.graph.nodes) {
					console.log("\t\tName:", nodeName);
					for (let j = 0; j < oneGraph.graph.nodes[nodeName].dependencies.length; j++) {
						console.log("\t\t\tDependency:", oneGraph.graph.nodes[nodeName].dependencies[j]);
					}
				}
			}
		}
	}

	/**
	 * Cleanup this stage's outstanding timers -- outstanding timers are only left if premature exit of stage on error
	 */
	private cleanupStagesOutStandingTimers() {
		Object.keys(this.startTimers).forEach((dependencyName) => {
			clearTimeout(this.startTimers[dependencyName]);
			delete this.startTimers[dependencyName];
		});
	}

	private processBootModuleType({
		bootParams,
		config,
		currentStage,
		name,
		type,
	}: {
		bootParams: any;
		config: any;
		currentStage: BootStage;
		name: string;
		type: string;
	}): { stage: BootStage; timeout: number } {
		if (type === "services") {
			const { debugServiceDelay } = config;
			const stage = bootParams.stage || "earlyuser";
			let timeout = bootParams.timeout || this.startServiceTimeout;
			// if the service has debugServiceDelay set, then delay the timeout value an equivalent amount so developer can set breakpoint in service)
			if (debugServiceDelay && stage === currentStage) {
				timeout = timeout + debugServiceDelay;
				SystemLog.warn(
					{ leadingBlankLine: false },
					`==> timeout value for ${name} will be increased to ${timeout} (debugServiceDelay=${debugServiceDelay})`
				);
			}
			return { stage, timeout };
		}

		if (type === "bootTasks") {
			const stage = bootParams.stage || "earlyuser";
			const timeout = bootParams.timeout || this.startTaskTimeout;
			return { stage, timeout };
		}

		if (type === "components") {
			const { component = {} } = config;
			const stage = bootParams.stage || "user";
			const conflictingStartupDirectives = bootParams.autoStart === false && component.spawnOnStartup;
			if (conflictingStartupDirectives && stage === currentStage) {
				SystemLog.error(
					{ leadingBlankLine: false },
					`BOOT CONFIG: autoStart conflicts with spawnOnStartup value for ${name} in ${type} -- using spawnOnStartup value`
				);
			}

			bootParams.autoStart = component.spawnOnStartup ?? false;
			const timeout = bootParams.timeout || this.startComponentTimeout;
			return { stage, timeout };
		}
		return { stage: "undefined", timeout: 60000 };
	}

	/**
	 * Returns an initialized boot config element, based on config data and documented defaults.
	 *
	 * Data validation is also done here, with errors sent to system log. This function ensures boot config elements used elsewhere always contain valid values (unless error generated here).
	 *
	 * @param currentStage the current bootstage (e.g. kernel)
	 * @param name the dependency name (e.g. service name)
	 * @param type the dependency type (e.g. "services") -- only needed to set default values
	 * @param config the corrsponding bootParms config data (e.g. finsemble.services.workspaceService.bootParams)
	 * @returns initialized boot-config element for one dependency
	 */
	private getBootConfigElement(
		currentStage: BootStage,
		name: string,
		type: BootDependencyType,
		originalConfig: any
	): BootConfigElement {
		// Note: this function does the final determination of the current stage of a config element, but then shouldn't output error messages if not current stage (so errors don't clutter SystemLog)

		// clone so won't modify the original config
		const configClone = Util.clone(originalConfig);

		let { bootParams } = configClone;

		// when bootParms are defined do some extra checking
		if (bootParams) {
			if (!ALL_BOOT_STAGES.includes(bootParams.stage)) {
				// put out an error message unless stage is null and autoStart is false (which would be a reasonable config)
				if (bootParams.stage || bootParams.autoStart !== false) {
					SystemLog.error(
						{ leadingBlankLine: false },
						`BOOT CONFIG: unknown boot stage "${bootParams.stage}" for ${name} in ${type} -- assigning default value`
					);
				}
				// set stage to null so will assign default value below
				bootParams.stage = null;
			}
		} else {
			bootParams = {};
		}

		const { stage, timeout } = this.processBootModuleType({
			bootParams,
			config: configClone,
			currentStage,
			name,
			type,
		});

		const { checkpointConfig = {}, autoStart = true, stopOnFailure = true, customFailureMessage = "" } = bootParams;

		// assign the rest of the values -- use defaults if config doesn't have specific value
		let dependencies: string[] = bootParams.dependencies || [];

		// Ensure dependencies is an array, given simple mistake to put in a string for a single dependency
		if (!Array.isArray(dependencies) && stage === currentStage) {
			SystemLog.error(
				{ leadingBlankLine: false },
				`BOOT CONFIG: bootparams.dependencies for ${name} in ${type} must be an array`
			);
			dependencies = [""];
		}

		// create new config item that will be filled out then returned
		const newConfigElement: BootConfigElement = {
			// if the name is specified within the config then use that name, just in case it is different from the property name
			name: originalConfig.name || name,
			type,
			stage,
			timeout,
			checkpointsIncluded: Object.keys(checkpointConfig).length > 0,
			// if there are checkpoints, carry then over for later processing by checkpoint engine
			checkpointConfig,
			stopOnFailure,
			autoStart,
			customFailureMessage,
			originalConfig,
			dependencies,
			postStartupCompletion: false,
		};

		return newConfigElement;
	}

	/**
	 * Adds to the cumulativeBootConfig array the boot-config elements for a given type within the specified stage (e.g. services within the kernal stage).
	 * @param currentStage current stage of booting
	 * @param type the dependency type (e.g. services) -- this is only need to set default values
	 * @param dependencyConfigs all the config data for given type (e.g. finsemble.tasks, finsemble.services)
	 * @param bootConfig the array to push the boot-config elements to for given stage and type
	 */
	private getBootConfigByType(
		currentStage: BootStage,
		type: BootDependencyType,
		dependencyConfigs: object,
		cumulativeBootConfig: BootConfigElement[]
	) {
		for (const property in dependencyConfigs) {
			const newConfigElement: BootConfigElement = this.getBootConfigElement(
				currentStage,
				property,
				type,
				dependencyConfigs[property]
			);
			// have to process boot config first before checking if right stage, because stage might not be set yet when default is used
			if (newConfigElement.stage == currentStage) {
				cumulativeBootConfig.push(newConfigElement);
			}
		}
	}

	/**
	 * Pulls together all the boot-config elements for one stage, including boot-config elements for tasks, services, and components
	 * @param stage the boot stage
	 * @param finsembleConfig current finsembleConfig (may be updated between stages)
	 * @returns the cumulative array of all the boot-config elements for the specified stage
	 */
	private getBootConfig(stage: BootStage, finsembleConfig: FinsembleConfig): BootConfigElement[] {
		const cumulativeBootConfig: BootConfigElement[] = []; // for each stage start with a clear array of boot-config elements

		for (let i = 0; i < ALL_BOOT_DEPENDENCY_TYPES.length; i++) {
			const dependencyType: BootDependencyType = ALL_BOOT_DEPENDENCY_TYPES[i];

			this.getBootConfigByType(stage, dependencyType, finsembleConfig[dependencyType], cumulativeBootConfig);

			// for service type, also do required services (which are in a different config location)
			if (dependencyType == "services") {
				this.getBootConfigByType(
					stage,
					dependencyType,
					finsembleConfig.system.requiredServicesConfig,
					cumulativeBootConfig
				);
			}
		}

		return cumulativeBootConfig;
	}

	/**
	 * Based on the depenency graph, asynchronously run through the boot sequence for one stage.  Will not stop until graphStage is "finished".
	 * The state of started dependencies will trigger new dependencies to be ready and started until finished.
	 *
	 * Here's the general flow:
	 * 1) a new dependency graph is created and any initial errors and ready-to-start dependencies are handled
	 * 2) as starting dependencies (e.g. service or boot task) communicate back, genericHandlerForStateChange is triggered
	 * 3) setBootDependencyState is then called to update the dependency state in the dependency graph
	 * 4) as states in the dependency tree's are changed to "completed", other dependencies become ready to start (see readyToStartList),
	 * 	  then those dependencies are started (which then triggers other state changes and so on)
	 * 5) when the stage's dependency tree is finished (either because everything is started or an error occurred), then the stage is complete
	 *
	 * @param stage the boot stage to run
	 * @returns Promise, which will be resolved or rejected in processWhatIsReady
	 */
	private runBootStage(stage: BootStage): Promise<any> {
		const runBootStagePromiseResolver = async (resolve, reject) => {
			try {
				SystemLog.log({ leadingBlankLine: false }, `BOOT STAGE: ${stage}`);
				this.stageResolver = resolve;
				this.stageRejecter = reject;

				this.bootConfigs[stage] = this.getBootConfig(stage, this.finsembleConfig);

				this.dependencyGraphs[stage] = new BootDependencyGraph(
					stage,
					this.bootConfigs[stage] ?? [],
					this.cumulativePreviousBootConfig
				);
				Logger.system.log(
					"forceObjectsToLogger",
					"BootDependencyGraph.runBootStage",
					stage,
					this.bootConfigs[stage],
					this.cumulativePreviousBootConfig,
					this.dependencyGraphs[stage]
				);
				console.log(
					"BootDependencyGraph.runBootStage",
					stage,
					this.bootConfigs[stage],
					this.cumulativePreviousBootConfig,
					this.dependencyGraphs[stage]
				);

				// kicks off processing the dependency graph for this stage -- will continue until done
				const dependencyGraph = this.dependencyGraphs[stage];
				if (dependencyGraph) this.processWhatIsReady(dependencyGraph);

				// keep list of all to check for dependency located in a previous state
				const bootConfig = this.bootConfigs[stage];
				if (bootConfig) this.cumulativePreviousBootConfig = [...this.cumulativePreviousBootConfig, ...bootConfig];
			} catch (err) {
				reject(new DGDiagnostics(`BootEngine:runBootStage.reject${err}`));
			}
		};
		return new Promise(runBootStagePromiseResolver);
	}

	/**
	 * Housekeeping function to setup boot pub sub for each router-based dependency
	 */
	private async setupDependencyBootStatusPubSubs(): Promise<void> {
		// this is called from initialize() so routerInitial tasks hasn't ran yet, so wait here until router ready.
		await new FSBLService().startApp();

		// Instead of invoking addPubSubResponder to set up dependency pubsubs and checkpoint pubsubs, instead use the built-in router service capability to do the same.
		// Using the router service's build-iun capability will also avoid the initial "uninitialized" notification that isn't properly handled.
		// RouterClient.addPubSubResponder(new RegExp(`${STATUS_CHANNEL_BASE}.*`), UNITIALIZED_BOOT_STATE);
		// RouterClient.addPubSubResponder(new RegExp(`${CHECKPOINT_CHANNEL_BASE}.*`), UNITIALIZED_BOOT_STATE);
		// RouterClient.addPubSubResponder(STAGE_CHANNEL, {	stage: "uninitialized" });
	}

	/**
	 * Housekeeping function to setup boot pub sub for each router-based dependency
	 */
	private subscribeToDependencyBootStatus(dependencyName: string): void {
		// receives startup state from services -- see SystemManagerClient.publishBootStatus.  Router is known to be ready before this is called.
		RouterClient.subscribe(statusChannel(dependencyName), (err, notify) => {
			if (err) {
				Logger.system.error("BootEngine subscribe error", err);
			} else {
				this.genericHandlerForStateChange(notify.data.name, notify.data.type, notify.data.state, notify.data.error);
			}
		});
	}

	private waitForLastCheckpoint(dependencyName: string, state: BootDependencyState) {
		// The service is now done, so time to get checkpoint status.
		// MM Note: This delay is to handle a rare race condition where the pubsub for the service-complete beats back the pubsub for the last checkpoint.
		// This can happens because checkpoint the subscribe doesn't occur until the checkpoint dependency is fullfilled, which might happen a bit after the actual
		// checkpoint fires in another window.  When this happens the subscribe's initial pubsub state (which is decoupled from the earlier publish) can be
		// received after the service complete and look like the checkpoint failed. I know how to eliminate this problem without using a delay,
		// but doing so may be more work than the problem merits -- so keeping this small delay as the fix. However, might revisit and eliminate delay when opportunity arises.
		setTimeout(() => {
			const checkpointResults: BootCheckpointResults = this.checkpointEngines[dependencyName].getResults(state);
			if (!checkpointResults.okay) {
				this.checkpointLogDiagnostics(checkpointResults.errorDiag);
			}

			// clean up, plus prevent multiple results from two state changes (i.e. first a timeout failure then later "completed" arrives)
			delete this.checkpointEngines[dependencyName];

			Logger.system.debug(
				"bootEngine.genericHandlerForStateChange checkpointEngine results",
				dependencyName,
				checkpointResults
			);
		}, WAIT_FOR_LAST_CHECKPOINT_DELAY);
	}

	/**
	 * Handles one dependency state change
	 * @param dependencyName
	 * @param type
	 * @param state
	 */
	private handleOneDependencyStateChange(
		dependencyName: string,
		type: BootDependencyType,
		state: BootDependencyState,
		error?: string
	): void {
		if (state === "completed" && type === "services") {
			// keep a list of all started/active services to use for shutdown
			this.activeServices.push(dependencyName);
		} else if (state === "completed" && type === "bootTasks") {
			// the status for services is published in base service and for components in FSBL, but since tasks are local to system manager will publish status here;
			// publish task status allow support for waitForStartup for tasks
			SystemManagerClient.publishBootStatus(dependencyName, "bootTasks", "completed");
		}

		// if changing to a final state then update dependency graph with the new state and continue processing
		if (state === "completed" || state === "failed") {
			const graph = this.dependencyGraphs[this.currentStage];

			// if checkpoint engine running for this dependency, then get the checkpoint status and display any errors
			if (this.checkpointEngines[dependencyName]) {
				this.waitForLastCheckpoint(dependencyName, state);
			}

			if (state === "failed") {
				const configElement = this.findConfigElement(dependencyName);
				SystemLog.error(
					{ doubleIndent: true },
					`BootState=${state} for ${dependencyName}. ${error}. ${configElement?.customFailureMessage}`
				);
			}

			if (graph) {
				graph.setBootDependencyState(dependencyName, state);
				this.processWhatIsReady(graph);
			}
		}
	}

	/**
	 * Generic handler for state changes of dependencies
	 * @param dependencyName
	 * @param state
	 */
	private genericHandlerForStateChange(
		dependencyName: string,
		type: BootDependencyType,
		state: BootDependencyState,
		error?: string
	): void {
		Logger.system.debug(`bootEngine.genericHandlerForStateChange ${state} for ${type} ${dependencyName}`);
		console.debug("bootEngine.genericHandlerForStateChange", dependencyName, type, state);

		// if timeout never fired, then stop timer and cleanup
		if (this.startTimers[dependencyName]) {
			clearTimeout(this.startTimers[dependencyName]);
			delete this.startTimers[dependencyName];
		}

		// if engine is still processing (didn't prematurely terminate on error) then process the state change
		if (!this.processingTerminated) {
			this.handleOneDependencyStateChange(dependencyName, type, state, error);
		}
	}

	/**
	 * Handles error timeout for a started dependency (i.e. no response was received)
	 * @param dependencyName
	 */
	private handleStartTimeout(dependencyName: string, type: BootDependencyType): void {
		delete this.startTimers[dependencyName]; // remove from active list of timers
		SystemLog.error({ indent: false }, `${dependencyName} ${type} startup failed due to a time out`);
		this.genericHandlerForStateChange(
			dependencyName,
			type,
			"failed",
			`${dependencyName} startup failed due to a time out`
		);
	}

	/**
	 * Starts dependency that's a boot task
	 * @param readyItem info for boot task to start
	 */
	private startBootTaskDependency(readyItem: BootReadyItem) {
		SystemLog.log({ indent: true }, `starting boot task: ${readyItem.name}`);
		Logger.system.debug(
			`bootEngine.startBootTaskDependency starting boot task ${readyItem.name} in ${this.currentStage}`,
			readyItem
		);
		if (this.registeredTasks[readyItem.name]) {
			// invoke the registered boot task
			const task = this.registeredTasks[readyItem.name];
			task?.((taskName: string, type: BootDependencyType, state: BootDependencyState) => {
				// SystemLog.log({ indent: true }, `finishing boot task: ${readyItem.name} ${state}`);
				// Force a processor break before invoking state change handler; this is done only for directly-invoked
				// tasks which might directly invoke their callback.  The purpose if so processing the ready list is
				// alway done breadth-first (never depth first). Note this forced break is not required, but it makes the
				// startup order more intuitive for user
				setTimeout(() => {
					this.genericHandlerForStateChange(taskName, type, state);
				}, 0);
			});
		} else {
			Logger.system.error(`bootEngine.startBootTaskDependency: unknown task ${readyItem.name}`, readyItem);
			SystemLog.error({}, `bootEngine.startBootTaskDependency: unknown task ${readyItem.name}`);
		}
	}

	/**
	 * Starts dependency that's a service
	 * @param readyItem info for service to start
	 */
	private async startServiceDependency(readyItem: BootReadyItem): StandardPromise<void> {
		SystemLog.log({ indent: true }, `starting service: ${readyItem.name}`);
		this.subscribeToDependencyBootStatus(readyItem.name);
		Logger.system.debug(
			`bootEngine.startServiceDependency starting service ${readyItem.name} in ${this.currentStage}`,
			readyItem
		);
		const result = await this.serviceLauncher.createService(readyItem.config.originalConfig);
		if (result.err) {
			const error = `Error starting service ${readyItem.name}: ${result.err}`;
			SystemLog.error({ indent: true }, error);
			Logger.system.error(error, readyItem);
		}
		return result;
	}

	/**
	 * Starts dependency that's a component. This dependency will be fulfilled in
	 * FSBLDesktop `signalSuccessfulStart()`.
	 * @param readyItem info for component to start
	 */
	private startComponentDependency(readyItem: BootReadyItem) {
		// components cannot be auto started in first two stages because the essential services are not up
		if (this.currentStage === "microkernel" || this.currentStage === "kernel") {
			SystemLog.error(
				{ indent: true },
				`cannot config in bootParams for component ${readyItem.name} to start until after "kernel" stage`
			);
		} else {
			let appOrComponent = readyItem.config.originalConfig.isAnApp ? "fdc3 app" : "component";
			SystemLog.log({ indent: true }, `starting ${appOrComponent}: ${readyItem.name}`);
			this.subscribeToDependencyBootStatus(readyItem.name);
			Logger.system.debug(
				`bootEngine.startComponentDependency starting ${appOrComponent} ${readyItem.name} in ${this.currentStage}`,
				readyItem,
				readyItem.config.originalConfig
			);
			LauncherClient.spawn(readyItem.name, {});
		}
	}

	/**
	 * Starts a ready dependency -- one that moved to the "readyToStart" state in the dependency tree when all of its dependencies became ready.
	 * If ready dependency has checkpoints, then also startup corresponding checkpoint engine for those checkpoints
	 * @param listItem list of ready dependencies to start
	 */
	private startReadyItem(readyItem: BootReadyItem) {
		// if the dependency being started doesn't respond then this handles the timeout error
		this.startTimers[readyItem.name] = setTimeout(() => {
			this.handleStartTimeout(readyItem.name, readyItem.type);
		}, readyItem.config.timeout);

		switch (readyItem.type) {
			case "bootTasks": {
				this.startBootTaskDependency(readyItem);
				break;
			}
			case "services": {
				this.startServiceDependency(readyItem);
				break;
			}
			case "components": {
				this.startComponentDependency(readyItem);
				break;
			}
			default:
				Logger.system.error("bootEngine.startReadyItem: unknown boot type", readyItem); // this should never happen
		}

		// if the dependency being started has checkpoint, then create an instance of checkpointEngine to process those checkpoints;
		// then save the checkpointEngine so it can be queried for results when the dependency is finished (i.e. "completed" or "failed");
		// note the checkpoints do not affect functionality -- they are only to improve diagnostics when startup errors occur
		if (readyItem.config.checkpointsIncluded) {
			Logger.system.debug("bootEngine.startReadyItem new BootCheckpointEngine", readyItem.config.name, readyItem);
			if (readyItem.config.name) {
				this.checkpointEngines[readyItem.config.name] = new BootCheckpointEngine(
					readyItem.config.name,
					this.currentStage,
					readyItem.config.timeout ?? 1000000,
					readyItem.config.checkpointConfig
				);
			}
		}
	}

	/**
	 * Sets state in dependency tree for a list of dependencies.  Note changing these states affects the status of the dependency tree
	 * @param readyList
	 * @param newState
	 */
	private setStateForList(readyList: BootReadyItem[], newState: BootDependencyState) {
		for (let i = 0; i < readyList.length; i++) {
			const listItem: BootReadyItem = readyList[i];
			const stage = this.dependencyGraphs[this.currentStage];
			if (stage) stage.setBootDependencyState(listItem.name, newState);
		}
	}

	/**
	 * Process the ready list (i.e. what's ready to start) -- the ready list is always returned in the status of the dependency tree
	 * @param readyList the ready list of what to start
	 */
	private processReadyList(readyList: BootReadyItem[]) {
		Logger.system.debug("bootEngine.processReadyList", readyList);

		// change state in readyList from "readyToStart" to "starting"; have to do it upfront to avoid potential asynchronous issues with genericHandlerForStateChange
		this.setStateForList(readyList, "starting");

		for (let i = 0; i < readyList.length; i++) {
			const listItem: BootReadyItem = readyList[i];
			this.startReadyItem(listItem);
		}
	}

	/**
	 * Process the dependency tree, which typically means getting its latest status then starting all the dependencies that are ready.
	 * However, if the status indicates the graph is finished (typically meaning everything has started) then accept this stage's promise -- the stage is complete.
	 * Or if the status indicates an error occurred, then reject this stages promise (ending this stage)
	 * @param graph the current stages dependency graph
	 */
	private processWhatIsReady(graph: BootDependencyGraph): void {
		const status: DGGraphStatus = graph.getCurrentStatus();
		Logger.system.debug("bootEngine processWhatIsReady", status, graph);
		console.log("processWhatIsReady", status);

		// this condition should never happen, but would prevent bad bootEngine handlers from getting in an infinite loop
		if (++this.processStatusCounter > PROCESS_STATUS_COUNTER_MAX) {
			Logger.system.error(
				"bootEngine.processWhatIsReady is in infinite loop -- breaking out",
				this.processStatusCounter
			);
		} else {
			switch (status.graphState) {
				case "error": {
					if (this.stageRejecter) this.stageRejecter(status.errorDiag);
					break;
				}
				case "notFinished": {
					this.processReadyList(status.readyToStartList);
					break;
				}
				case "finished": {
					if (this.stageResolver) this.stageResolver();
					break;
				}
				default:
					break;
			}
		}
	}
}
